import type { ReactNode } from 'react';

export type MeterTone = 'primary' | 'warning' | 'destructive' | 'muted';

const FILL_TONE: Record<MeterTone, string> = {
  // bg-primary on the track's bg-secondary is 1.44:1 in light theme, under
  // WCAG 1.4.11's 3:1 floor for a non-text graphical object (dark clears it
  // at 9.94:1). border-primary-ink is the visible boundary that substitutes
  // for direct fill/track contrast -- same shape as tokens.css's
  // --floor/--above-floor stacked bar, and the same border button.tsx's
  // default variant already draws around this same fill: it clears 3:1
  // against bg-primary (3.80:1 light) and against bg-secondary (5.48:1
  // light) on its own.
  primary: 'border border-primary-ink bg-primary',
  warning: 'bg-utilisation-mid',
  destructive: 'bg-destructive',
  muted: 'bg-muted-foreground',
};

const TRACK = 'h-track w-full rounded-pill bg-secondary';

export function MeterBar(props: {
  value: number | null;
  max: number;
  tone?: MeterTone;
  label: string;
}): ReactNode {
  const { value, max, tone = 'primary', label } = props;

  // §4.5's absence rule: a bar with nothing to show renders the track alone,
  // never a full one -- an empty meter and a full meter look identical otherwise.
  // `role="meter"` requires `aria-valuenow` (axe's aria-required-attr), and
  // this codebase's own lint bans standing zero in for absence, so a value-less
  // *or scale-less* bar drops the meter role entirely rather than claiming an
  // ARIA range where max < min (a non-positive max) or a non-finite figure.
  if (value === null || max <= 0 || !Number.isFinite(value)) {
    return <div role="img" aria-label={label} className={TRACK} />;
  }

  // A value past the max draws a full track and must not announce a figure
  // its own max excludes.
  const clamped = Math.min(Math.max(value, 0), max);
  const fraction = clamped / max;
  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={clamped}
      className={TRACK}
    >
      <div
        data-slot="fill"
        className={`h-track rounded-pill ${FILL_TONE[tone]}`}
        style={{ width: `${fraction * 100}%` }}
      />
    </div>
  );
}
